import React from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Box, Typography, CircularProgress, Alert } from "@mui/material";
import ClassOverview from "../../components/classOverview/ClassOverview";
import ConfirmDialog from "../../components/ConfirmDialog";
import { useTenant } from "../../contexts/TenantContext";
import useCoursePermissions from "../../hooks/useCoursePermissions";

import {
  fetchClassById,
  fetchSessionsForCourse,
  fetchChecklistsForCourse,
  fetchTestsForCourse,
  fetchClassLevelContent,
  fetchSessionContent,
  fetchCourseDocumentAssignments,
  fetchUsersByIds,
  fetchLearningPaths,
  fetchLearningPlan,
  completeClass,
} from "../../api/backend";
import { countActive } from "../contentIsActiveHelpers";

import DetailsTab from "./DetailsTab";
import RosterTab from "./RosterTab";
import SessionsTab from "./SessionsTab";
import SessionContentTab from "./DocumentsTab";
import SkillChecklistsTab from "./SkillChecklistsTab";
import WrittenTestsTab from "./WrittenTestsTab";
import VirtualLearningTab from "./VirtualLearningTab";
import ReportsTab from "./ReportsTab";
import InstructorsTab from "./InstructorsTab";

const TAB_ROUTES = [
  "",
  "details",
  "instructors",
  "sessions",
  "attendance",
  "session-content",
  "checklists",
  "tests",
  "reports",
  "virtual-learning",
];
function TabIcon({ children }) {
  return (
    <Box component="span" sx={{ display: "inline-flex", alignItems: "center" }}>
      {children}
    </Box>
  );
}

const TAB_ICONS = [
  // Roster — people
  <TabIcon key="roster">
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  </TabIcon>,
  // Details — info
  <TabIcon key="details">
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  </TabIcon>,
  // Instructors — user
  <TabIcon key="instructors">
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  </TabIcon>,
  // Sessions — calendar
  <TabIcon key="sessions">
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  </TabIcon>,
  // Attendance — checkmark
  <TabIcon key="attendance">
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  </TabIcon>,
  // Documents — file
  <TabIcon key="documents">
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
    </svg>
  </TabIcon>,
  // Skill Check Sheets — checklist
  <TabIcon key="checklists">
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="9 11 12 14 22 4" />
      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
    </svg>
  </TabIcon>,
  // Tests — waveform
  <TabIcon key="tests">
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
    </svg>
  </TabIcon>,
  // Reports — bar chart
  <TabIcon key="reports">
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="18" y1="20" x2="18" y2="10" />
      <line x1="12" y1="20" x2="12" y2="4" />
      <line x1="6" y1="20" x2="6" y2="14" />
    </svg>
  </TabIcon>,
  // Virtual Learning — ordered content paths
  <TabIcon key="virtual-learning">
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="8" y1="6" x2="21" y2="6" />
      <line x1="8" y1="12" x2="21" y2="12" />
      <line x1="8" y1="18" x2="21" y2="18" />
      <line x1="3" y1="6" x2="3.01" y2="6" />
      <line x1="3" y1="12" x2="3.01" y2="12" />
      <line x1="3" y1="18" x2="3.01" y2="18" />
    </svg>
  </TabIcon>,
];

export default function CourseHubPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = location.pathname.startsWith("/course-management")
    ? "/course-management"
    : "/course-hub";
  const { effectiveUser, isFeatureEnabled } = useTenant();
  const contentEnabled = isFeatureEnabled("instructorClassContent");
  const [course, setCourse] = React.useState(null);
  const [courseInstructors, setCourseInstructors] = React.useState([]);
  const [sessions, setSessions] = React.useState([]);
  const [checklists, setChecklists] = React.useState([]);
  const [tests, setTests] = React.useState([]);
  const [documentsCount, setDocumentsCount] = React.useState(0);
  const [vlpCount, setVlpCount] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [completeOpen, setCompleteOpen] = React.useState(false);

  const { isInstructor, isCourseCoordinator, isAdmin } = useCoursePermissions(
    effectiveUser,
    course,
  );

  const canViewDetails = isCourseCoordinator || isAdmin;
  const canViewInstructors = isCourseCoordinator || isAdmin;
  const canEditDetails = canViewDetails && course?.lifecycleStatus !== "COMPLETED";
  const canEditInstructors = canViewInstructors && course?.lifecycleStatus !== "COMPLETED";
  const canEditSessions = (isCourseCoordinator || isAdmin) && course?.lifecycleStatus !== "COMPLETED";
  const canToggleSessionEnabled =
    isInstructor || isCourseCoordinator || isAdmin;
  const canViewSessions = canEditSessions || canToggleSessionEnabled;
  const canEditAttendance = (isInstructor || isAdmin) && course?.lifecycleStatus !== "COMPLETED";
  const canEditSessionContent =
    (isCourseCoordinator || isAdmin) && contentEnabled && course?.lifecycleStatus !== "COMPLETED";
  const canEditSkillChecklists =
    (isCourseCoordinator || isAdmin) && contentEnabled && course?.lifecycleStatus !== "COMPLETED";
  const canEditScenarioTests =
    (isCourseCoordinator || isAdmin) && contentEnabled && course?.lifecycleStatus !== "COMPLETED";
  // Instructors can still view documents, conduct skill assessments, and review tests —
  // they just can't upload/attach/build content. Tab visibility uses these broader
  // view flags; canEdit (above) gates the upload/attach actions within each tab.
  const canViewSessionContent =
    (isInstructor || isCourseCoordinator || isAdmin) && contentEnabled;
  const canViewSkillChecklists =
    (isInstructor || isCourseCoordinator || isAdmin) && contentEnabled;
  const canViewScenarioTests =
    (isInstructor || isCourseCoordinator || isAdmin) && contentEnabled;

  // Define all tabs with their access requirements
  const allTabs = [
    {
      index: 0,
      route: "",
      label: "Roster",
      icon: TAB_ICONS[0],
      canAccess: true,
    },
    {
      index: 1,
      route: "details",
      label: "Details",
      icon: TAB_ICONS[1],
      canAccess: canViewDetails,
    },
    {
      index: 2,
      route: "instructors",
      label: "Instructors",
      icon: TAB_ICONS[2],
      canAccess: canViewInstructors,
    },
    {
      index: 3,
      route: "sessions",
      label: "Sessions",
      icon: TAB_ICONS[3],
      canAccess: canViewSessions,
    },
    {
      index: 5,
      route: "session-content",
      label: "Documents",
      icon: TAB_ICONS[5],
      canAccess: canViewSessionContent,
    },
    {
      index: 6,
      route: "checklists",
      label: "Skill Check Sheets",
      icon: TAB_ICONS[6],
      canAccess: canViewSkillChecklists,
    },
    {
      index: 7,
      route: "tests",
      label: "Tests",
      icon: TAB_ICONS[7],
      canAccess: canViewScenarioTests,
    },
    {
      index: 9,
      route: "virtual-learning",
      label: "Virtual Learning",
      icon: TAB_ICONS[9],
      canAccess: canViewSessionContent,
    },
    {
      index: 8,
      route: "reports",
      label: "Reports",
      icon: TAB_ICONS[8],
      canAccess: true,
    },
  ];

  // Filter accessible tabs
  const accessibleTabs = allTabs.filter((tab) => tab.canAccess);

  // Derive active tab from URL
  const pathSegments = location.pathname.split("/").filter(Boolean);
  const lastSegment = pathSegments[pathSegments.length - 1];
  const originalTabIndex =
    TAB_ROUTES.indexOf(lastSegment) === -1
      ? lastSegment === id
        ? 0
        : 0
      : TAB_ROUTES.indexOf(lastSegment);

  React.useEffect(() => {
    setLoading(true);
    Promise.all([
      fetchClassById(id),
      fetchSessionsForCourse(id),
      fetchChecklistsForCourse(id),
      fetchTestsForCourse(id),
    ]).then(([cls, sess, chk, tsts]) => {
      setCourse(cls);
      setSessions(sess || []);
      setChecklists(chk || []);
      setTests(tsts || []);
      setLoading(false);
    });
  }, [id]);

  React.useEffect(() => {
    if (!course) {
      setCourseInstructors([]);
      return;
    }

    const uniqueIds = course.leadInstructorId ? [course.leadInstructorId] : [];

    if (uniqueIds.length === 0) {
      setCourseInstructors([]);
      return;
    }

    fetchUsersByIds(uniqueIds)
      .then((users) => {
        setCourseInstructors(Array.isArray(users) ? users : []);
      })
      .catch((err) => {
        console.error("Failed to fetch course instructors:", err);
        setCourseInstructors([]);
      });
  }, [course]);

  React.useEffect(() => {
    if (!id) {
      setDocumentsCount(0);
      return;
    }

    let cancelled = false;

    const loadDocumentCount = async () => {
      try {
        const classDocsPromise = fetchClassLevelContent(id).catch(() => []);
        const bankDocsPromise = fetchCourseDocumentAssignments(id).catch(
          () => [],
        );
        const sessionDocsPromises = (sessions || []).map((s) =>
          fetchSessionContent(s.id).catch(() => []),
        );

        const [classDocs, bankDocs, ...sessionDocsLists] = await Promise.all([
          classDocsPromise,
          bankDocsPromise,
          ...sessionDocsPromises,
        ]);

        if (cancelled) return;

        const sessionsById = Object.fromEntries(
          (sessions || []).map((s) => [String(s.id), s]),
        );
        const allDocs = [
          ...(Array.isArray(classDocs) ? classDocs : []),
          ...(Array.isArray(bankDocs) ? bankDocs : []),
          ...sessionDocsLists.flatMap((l) => (Array.isArray(l) ? l : [])),
        ];

        setDocumentsCount(countActive(allDocs, { sessionsById }));
      } catch {
        if (!cancelled) setDocumentsCount(0);
      }
    };

    loadDocumentCount();

    return () => {
      cancelled = true;
    };
  }, [id, sessions]);

  React.useEffect(() => {
    if (!id) {
      setVlpCount(0);
      return;
    }
    let cancelled = false;
    (async () => {
      const paths = await fetchLearningPaths(id).catch(() => []);
      const plans = await Promise.all(
        (Array.isArray(paths) ? paths : []).map((p) =>
          fetchLearningPlan(id, p.id).catch(() => null),
        ),
      );
      if (cancelled) return;
      const total = plans.reduce(
        (sum, plan) =>
          sum + (Array.isArray(plan?.items) ? plan.items.length : 0),
        0,
      );
      setVlpCount(total);
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Refresh course data (called after sessions are modified to sync schedule)
  const refreshCourse = React.useCallback(() => {
    fetchClassById(id).then((cls) => {
      setCourse(cls);
    });
  }, [id]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!course) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography color="error">Course not found.</Typography>
      </Box>
    );
  }

  // Tab badge values
  const totalStudents = course.studentCount ?? course.enrolledStudents ?? 0;
  const sessionCount = sessions.length;
  const hasLiveSession = sessions.some((s) => s.enabled === true);

  const leadInstructor = courseInstructors.find(
    (i) => i.id === course.leadInstructorId,
  );
  const leadInstructorName =
    leadInstructor?.fullName ||
    leadInstructor?.name ||
    leadInstructor?.username ||
    "Unassigned";

  const canRegisterStudents = (isCourseCoordinator || isAdmin) && course?.lifecycleStatus !== "COMPLETED";
  // An instructor closes the class they teach once it has run. The server checks the assignment; a draft or a closed class has nothing to close.
  const canMarkComplete = isInstructor && course?.publishStatus !== "DRAFT" && course?.lifecycleStatus !== "COMPLETED";

  const sessionsById = Object.fromEntries(
    sessions.map((s) => [String(s.id), s]),
  );
  const checklistsActiveCount = countActive(checklists, { sessionsById });
  const testsActiveCount = countActive(tests, { sessionsById });

  const tabProps = {
    course,
    courseInstructors,
    sessions,
    setSessions,
    checklists,
    setChecklists,
    tests,
    setTests,
    refreshCourse,
    canEditDetails,
    canEditInstructors,
    canEditSessions,
    canEditAttendance,
    canEditSessionContent,
    canEditSkillChecklists,
    canEditScenarioTests,
    canRegisterStudents,
  };

  return (
    <>
    <ClassOverview
      breadcrumbLabel="Course Management"
      onBreadcrumbClick={() => navigate("/course-management")}
      title={course.title}
      banner={course.lifecycleStatus === "COMPLETED" ? (
        <Alert severity="info" sx={{ mb: 2 }}>This class is completed — view only.</Alert>
      ) : undefined}
      chips={[
        ...(course.courseNumber
          ? [{ label: course.courseNumber, mono: true }]
          : []),
        ...(course.category ? [{ label: course.category }] : []),
      ]}
      subtitle={`Lead Instructor · ${leadInstructorName}`}
      stats={[]}
      actions={canMarkComplete ? [{ key: "complete", label: "Mark class complete", onClick: () => setCompleteOpen(true) }] : []}
      tabs={accessibleTabs.map((tab) => ({
        key: tab.route || "roster",
        label: tab.label,
        icon: tab.icon,
        live: tab.index === 3 ? hasLiveSession : false,
        count:
          tab.index === 0
            ? totalStudents
            : tab.index === 3
              ? sessionCount
              : tab.index === 5
                ? documentsCount
                : tab.index === 6
                  ? checklistsActiveCount || null
                  : tab.index === 7
                    ? testsActiveCount || null
                    : tab.index === 9
                      ? vlpCount || null
                      : null,
      }))}
      activeTab={TAB_ROUTES[originalTabIndex] || "roster"}
      onTabChange={(key) => {
        const segment = key === "roster" ? "" : key;
        navigate(
          segment ? `${basePath}/${id}/${segment}` : `${basePath}/${id}`,
        );
      }}
    >
      {(originalTabIndex === 0 || originalTabIndex === 4) && (
        <RosterTab {...tabProps} />
      )}
      {originalTabIndex === 1 && (
        <DetailsTab course={course} canEdit={canEditDetails} />
      )}
      {originalTabIndex === 2 && (
        <InstructorsTab course={course} canEdit={canEditInstructors} />
      )}
      {originalTabIndex === 3 && (
        <SessionsTab
          sessions={sessions}
          setSessions={setSessions}
          refreshCourse={refreshCourse}
          canEdit={canEditSessions}
          canToggleEnabled={canToggleSessionEnabled}
          canEditAttendance={canEditAttendance}
          course={course}
          isInstructor={isInstructor}
          currentUserId={effectiveUser?.id}
          checklists={checklists}
          tests={tests}
        />
      )}
      {originalTabIndex === 5 && (
        <SessionContentTab
          sessions={sessions}
          canEdit={canEditSessionContent}
          onDocumentsCountChange={setDocumentsCount}
        />
      )}
      {originalTabIndex === 6 && (
        <SkillChecklistsTab {...tabProps} canEdit={canEditSkillChecklists} />
      )}
      {originalTabIndex === 7 && (
        <WrittenTestsTab {...tabProps} canEdit={canEditScenarioTests} />
      )}
      {originalTabIndex === 9 && (
        <VirtualLearningTab sessions={sessions} canEdit={canEditSessionContent} />
      )}
      {originalTabIndex === 8 && <ReportsTab {...tabProps} />}
    </ClassOverview>
    <ConfirmDialog
      open={completeOpen}
      title="Mark class complete?"
      message={<>
        <b>“{course.title}”</b> will be closed and become read-only. Certificates and AHA eCards can then be sent from the roster
        to the students who were signed off. Sign off every student who passed before you close the class.
      </>}
      confirmLabel="Mark complete"
      busyLabel="Completing…"
      danger={false}
      onConfirm={async () => {
        await completeClass(course.id, course.tenantId || effectiveUser?.tenantId);
        refreshCourse();
      }}
      onClose={() => setCompleteOpen(false)}
    />
    </>
  );
}
